import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Check, AlertTriangle, Minus, Plus, Trash2, GripVertical } from "lucide-react";
import { CADENZE, FASCE_MINUTI, PASSO_MINUTI, durataLeggibile, fraMesi } from "@/lib/costi";

/** La data come si scrive in italiano. */
const itDate = (iso) => {
  if (!iso) return "";
  const [a, m, g] = String(iso).substring(0, 10).split("-");
  return a && m && g ? `${g}/${m}/${a}` : String(iso);
};

const ESITI = [
  { valore: "ok", etichetta: "A posto", Icona: Check,
    attivo: "bg-emerald-500 text-white border-emerald-500" },
  { valore: "anomalia", etichetta: "Anomalia", Icona: AlertTriangle,
    attivo: "bg-red-500 text-white border-red-500" },
  { valore: "non_applicabile", etichetta: "Non serve", Icona: Minus,
    attivo: "bg-slate-400 text-white border-slate-400" },
];

/**
 * Il controllo interno: lo fa il personale dell'azienda. Si spuntano le voci
 * della scheda, si dice quando, chi, quanto ci ha messo e cosa segnavano il
 * contaore e il contachilometri. Alla fine diventa una riga del libro
 * manutenzione.
 *
 * Quando va rifatto lo decide chi lo esegue, qui: una cadenza — tre, quattro,
 * sei o dodici mesi — oppure una data precisa. Prima la scadenza usciva
 * sempre dall'intervallo scritto sulla scheda della macchina, e da qui non si
 * poteva dire niente.
 *
 * Una riparazione o una sostituzione non passa di qui: non ha spunte da fare,
 * e ha il suo modulo, «Nuovo intervento di manutenzione» — che poi la si sia
 * fatta in casa o l'abbia fatta un fornitore.
 *
 * Con `log` la stessa scheda si riapre su un controllo gia' registrato, per
 * correggerlo.
 */
export function ControlloInternoDialog({ eq, log, onClose, onSaved }) {
  const correzione = !!log;
  const oggi = new Date().toISOString().substring(0, 10);
  const [data, setData] = useState((log?.date || oggi).substring(0, 10));
  const [voci, setVoci] = useState(
    log?.checklist?.length
      ? log.checklist.map(v => ({ voce: v.voce, esito: v.esito || "ok", nota: v.nota || "" }))
      : (eq.checklist_manutenzione || []).map(v => ({ voce: v, esito: "ok", nota: "" })));
  const [dipendenti, setDipendenti] = useState([]);
  const [eseguitoDaId, setEseguitoDaId] = useState(log?.eseguito_da_id || "");
  const [minuti, setMinuti] = useState(String(log?.minuti || PASSO_MINUTI));
  const [ore, setOre] = useState(log?.ore_contaore ?? log?.ore ?? "");
  const [km, setKm] = useState(log?.km ?? "");
  const [note, setNote] = useState(log?.note || "");
  // Quando va rifatto. Si parte dalla cadenza della macchina, se ne ha una:
  // e' quella che si usa quasi sempre, e cosi' non c'e' niente da toccare.
  const cadenzaScheda = CADENZE.some(c => c.mesi === eq.maintenance_interval_months)
    ? eq.maintenance_interval_months : null;
  const [cadenza, setCadenza] = useState(
    log?.cadenza_mesi || (log ? "data" : cadenzaScheda) || "data");
  const [prossimo, setProssimo] = useState((log?.next_due_date || "").substring(0, 10));
  const [busy, setBusy] = useState(false);

  // La data che l'app propone: cambia con la cadenza e con la data del
  // controllo, cosi' si vede subito quando si tornera' sulla macchina.
  const scadenza = cadenza === "data" ? prossimo : fraMesi(data, Number(cadenza));

  useEffect(() => {
    api.get("/employees").then(({ data }) => setDipendenti(data)).catch(() => {});
  }, []);

  const cambiaEsito = (i, esito) =>
    setVoci(prev => prev.map((v, j) => (j === i ? { ...v, esito } : v)));
  const cambiaNota = (i, nota) =>
    setVoci(prev => prev.map((v, j) => (j === i ? { ...v, nota } : v)));

  const anomalie = voci.filter(v => v.esito === "anomalia");

  const salva = async () => {
    setBusy(true);
    const comuni = {
      date: data,
      voci: voci.map(v => ({ voce: v.voce, esito: v.esito, nota: v.nota || null })),
      eseguito_da_id: eseguitoDaId || null,
      type: "interno",
      minuti: parseInt(minuti, 10),
      km: km === "" ? null : parseInt(km, 10),
      cadenza_mesi: cadenza === "data" ? null : Number(cadenza),
      next_due_date: cadenza === "data" ? (prossimo || null) : null,
      note: note || null,
    };
    try {
      if (correzione) {
        await api.put(`/equipment/maintenance/${log.id}`, {
          ...comuni, ore: ore === "" ? null : parseInt(ore, 10),
        });
        toast.success("Controllo corretto");
      } else {
        await api.post(`/equipment/${eq.id}/controllo`, {
          ...comuni, ore_contaore: ore === "" ? null : parseInt(ore, 10),
        });
        toast.success(anomalie.length
          ? `Controllo registrato con ${anomalie.length} anomalie`
          : "Controllo registrato");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {correzione ? "Correggi il controllo interno" : "Controllo interno"} — {eq.identifier}
          </DialogTitle>
        </DialogHeader>

        {voci.length === 0 ? (
          <p className="text-sm text-slate-500 py-4">
            Questa macchina non ha ancora un elenco di controlli. Aggiungilo dal
            pulsante <b>Modifica controlli</b> nel libro manutenzione.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label>Data</Label>
                <Input type="date" value={data} data-testid="controllo-data"
                       onChange={(e) => setData(e.target.value)} />
              </div>
              <div>
                <Label>Ore al contaore</Label>
                <Input type="number" inputMode="numeric" data-testid="controllo-ore"
                       placeholder="escavatori, compressori, generatori"
                       value={ore} onChange={(e) => setOre(e.target.value)} />
              </div>
              <div>
                <Label>Km</Label>
                <Input type="number" inputMode="numeric" data-testid="controllo-km"
                       placeholder="se il mezzo li ha"
                       value={km} onChange={(e) => setKm(e.target.value)} />
              </div>
            </div>

            {/* Il controllo lo fa il personale dell'azienda: si dichiara
                quanto tempo ci si e' messi e il costo lo calcola l'app alla
                tariffa oraria. Non c'e' un fornitore da mettere, perche' un
                controllo fatto fuori non e' un controllo: e' un intervento. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Tempo impiegato</Label>
                <select className="w-full h-10 border border-slate-200 rounded px-3 mt-1"
                        value={minuti} data-testid="controllo-minuti"
                        onChange={(e) => setMinuti(e.target.value)}>
                  {FASCE_MINUTI.map(m => (
                    <option key={m} value={m}>{durataLeggibile(m)}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Chi lo esegue</Label>
                <select className="w-full h-10 border border-slate-200 rounded px-3 mt-1"
                        value={eseguitoDaId} data-testid="controllo-chi"
                        onChange={(e) => setEseguitoDaId(e.target.value)}>
                  <option value="">Io (chi ha fatto l'accesso)</option>
                  {dipendenti.map(d => (
                    <option key={d.id} value={d.id}>{d.full_name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="border border-slate-200 rounded divide-y divide-slate-100">
              {voci.map((v, i) => (
                <div key={i} className="p-3 space-y-2">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <span className="text-sm flex-1 min-w-[180px]">{v.voce}</span>
                    <div className="flex gap-1 flex-shrink-0">
                      {ESITI.map(({ valore, etichetta, Icona, attivo }) => (
                        <button key={valore} type="button" onClick={() => cambiaEsito(i, valore)}
                                title={etichetta}
                                className={`px-2 py-1 rounded border text-[11px] flex items-center gap-1 ${
                                  v.esito === valore ? attivo
                                    : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"}`}>
                          <Icona className="w-3 h-3" />{etichetta}
                        </button>
                      ))}
                    </div>
                  </div>
                  {v.esito === "anomalia" && (
                    <Input placeholder="Cosa hai trovato?" value={v.nota}
                           onChange={(e) => cambiaNota(i, e.target.value)} />
                  )}
                </div>
              ))}
            </div>

            <div>
              <Label>Note</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)}
                     placeholder="facoltative" />
            </div>

            {/* Quando si torna sulla macchina. Lo decide chi fa il controllo:
                una cadenza, oppure una data che si sceglie. */}
            <div>
              <Label>Prossimo controllo</Label>
              <div className="flex gap-2 my-2 flex-wrap">
                {CADENZE.map(c => (
                  <button type="button" key={c.mesi} data-testid={"cadenza-" + c.mesi}
                          onClick={() => setCadenza(c.mesi)}
                          className={`px-3 py-1.5 rounded text-xs border ${
                            cadenza === c.mesi
                              ? "bg-orange-500 text-white border-orange-500"
                              : "bg-white border-slate-200 hover:border-slate-300"}`}>
                    {c.etichetta}
                  </button>
                ))}
                <button type="button" onClick={() => setCadenza("data")}
                        data-testid="cadenza-data"
                        className={`px-3 py-1.5 rounded text-xs border ${
                          cadenza === "data"
                            ? "bg-orange-500 text-white border-orange-500"
                            : "bg-white border-slate-200 hover:border-slate-300"}`}>
                  In una data precisa
                </button>
              </div>
              {cadenza === "data" ? (
                <Input type="date" value={prossimo} data-testid="controllo-prossimo"
                       onChange={(e) => setProssimo(e.target.value)} />
              ) : (
                <p className="text-xs text-slate-500">
                  Il prossimo controllo cade il <b>{itDate(scadenza) || "—"}</b>,
                  e questa cadenza resta sulla scheda della macchina.
                </p>
              )}
            </div>

            <div className={`text-xs rounded p-2 border ${anomalie.length
              ? "bg-red-50 border-red-200 text-red-800"
              : "bg-emerald-50 border-emerald-200 text-emerald-800"}`}>
              {anomalie.length
                ? `${anomalie.length} anomalie su ${voci.length} voci.`
                : `Tutte le ${voci.length} voci a posto.`}
              {scadenza
                ? ` Prossimo controllo il ${itDate(scadenza)}.`
                : " Nessuna data per il prossimo controllo: la scadenza non verrà aggiornata."}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Annulla</Button>
              <Button onClick={salva} disabled={busy}
                      className="bg-orange-500 hover:bg-orange-600 text-white">
                {busy ? "Salvataggio..."
                 : (correzione ? "Salva le correzioni" : "Registra il controllo")}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Editor dell'elenco controlli della macchina. */
export function ModificaChecklistDialog({ eq, onClose, onSaved }) {
  const [voci, setVoci] = useState(eq.checklist_manutenzione || []);
  const [nuova, setNuova] = useState("");
  const [busy, setBusy] = useState(false);

  const aggiungi = () => {
    const v = nuova.trim();
    if (!v) return;
    if (voci.some(x => x.toUpperCase() === v.toUpperCase())) {
      toast.error("Questa voce c'è già");
      return;
    }
    setVoci([...voci, v]);
    setNuova("");
  };

  const salva = async () => {
    setBusy(true);
    try {
      await api.put(`/equipment/${eq.id}/checklist`, { voci });
      toast.success("Elenco controlli aggiornato");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Controlli interni — {eq.identifier}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            Sono le voci che compaiono da spuntare quando si esegue il controllo.
            Arrivano dal libro di manutenzione, ma puoi cambiarle.
          </p>

          <div className="flex gap-2">
            <Input value={nuova} onChange={(e) => setNuova(e.target.value)}
                   placeholder="Nuova voce di controllo"
                   onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); aggiungi(); } }} />
            <Button variant="outline" onClick={aggiungi} className="flex-shrink-0">
              <Plus className="w-4 h-4" />
            </Button>
          </div>

          {voci.length === 0
            ? <p className="text-sm text-slate-500 py-2">Nessuna voce.</p>
            : (
              <div className="border border-slate-200 rounded divide-y divide-slate-100">
                {voci.map((v, i) => (
                  <div key={i} className="p-2 flex items-center gap-2">
                    <GripVertical className="w-3 h-3 text-slate-300 flex-shrink-0" />
                    <span className="text-sm flex-1">{v}</span>
                    <button type="button" title="Sposta su" disabled={i === 0}
                            onClick={() => setVoci(prev => {
                              const c = [...prev];
                              [c[i - 1], c[i]] = [c[i], c[i - 1]];
                              return c;
                            })}
                            className="text-xs text-slate-400 hover:text-slate-700 disabled:opacity-30 px-1">↑</button>
                    <button type="button" title="Sposta giù" disabled={i === voci.length - 1}
                            onClick={() => setVoci(prev => {
                              const c = [...prev];
                              [c[i + 1], c[i]] = [c[i], c[i + 1]];
                              return c;
                            })}
                            className="text-xs text-slate-400 hover:text-slate-700 disabled:opacity-30 px-1">↓</button>
                    <button type="button" title="Togli"
                            onClick={() => setVoci(prev => prev.filter((_, j) => j !== i))}
                            className="text-red-500 hover:text-red-700 px-1">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Annulla</Button>
            <Button onClick={salva} disabled={busy}
                    className="bg-orange-500 hover:bg-orange-600 text-white">
              {busy ? "Salvataggio..." : `Salva ${voci.length} voci`}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
